import { captureException } from '@sentry/react'

interface PlayMediaSafelyOptions {
  ignoreNotAllowedError?: boolean
}

const isAbortMediaPlayError = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === 'AbortError'
}

const isNotAllowedMediaPlayError = (error: unknown): boolean => {
  return error instanceof DOMException && error.name === 'NotAllowedError'
}

const reportUnexpectedMediaPlayError = (
  error: unknown,
  mediaElement: HTMLMediaElement
): void => {
  captureException(error, {
    level: 'error',
    tags: {
      module: 'media-playback',
      elementTag: mediaElement.tagName.toLowerCase(),
    },
    extra: {
      currentSrc: mediaElement.currentSrc,
      networkState: mediaElement.networkState,
      readyState: mediaElement.readyState,
      muted: mediaElement.muted,
      autoplay: mediaElement.autoplay,
      loop: mediaElement.loop,
    },
  })
}

/**
 * Browsers may reject media play promises when playback is interrupted by lifecycle changes.
 * This helper suppresses expected interruption errors and reports unexpected failures.
 */
export const playMediaSafely = (
  mediaElement?: HTMLMediaElement | null,
  options: PlayMediaSafelyOptions = {}
): void => {
  if (!mediaElement) {
    return
  }

  const shouldIgnoreError = (error: unknown): boolean => {
    if (isAbortMediaPlayError(error)) {
      return true
    }

    if (options.ignoreNotAllowedError && isNotAllowedMediaPlayError(error)) {
      return true
    }

    return false
  }

  try {
    const playPromise = mediaElement.play()

    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((error: unknown) => {
        if (shouldIgnoreError(error)) {
          return
        }

        reportUnexpectedMediaPlayError(error, mediaElement)
      })
    }
  } catch (error) {
    if (shouldIgnoreError(error)) {
      return
    }

    reportUnexpectedMediaPlayError(error, mediaElement)
  }
}
